import {
  ApiError,
  AppConfigSchema,
  type BatchTask, type BatchOverviewResponse,
  type ListBatchesRequest, type ListBatchesResponse,
  type GetBatchRequest, type GetBatchResponse,
  type CreateBatchRequest, type CreateBatchResponse,
  type RetryBatchRequest, type RetryBatchResponse,
  type CancelBatchRequest, type CancelBatchResponse,
  type ListAssetsRequest, type ListAssetsResponse,
  type RequestUploadUrlRequest, type RequestUploadUrlResponse,
  type ConfirmUploadRequest, type ConfirmUploadResponse,
  type Asset, type AppConfig, type Ok,
  type GetMeResponse, type RedeemInviteRequest, type RedeemInviteResponse,
} from '@avs/contract';
import type { ApiClient } from './types.js';

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** 用相对天数倒推，避免每次刷新时间不同造成视觉抖动 */
function isoDaysAgo(d: number, h = 12): string {
  // 用一个固定 epoch（Slice 完成日）+ 偏移，保证 mock 数据相对时间稳定
  const base = new Date('2026-06-02T18:00:00.000Z').getTime();
  return new Date(base - d * 86400_000 - h * 3600_000).toISOString();
}

const BATCH_SEED: BatchTask[] = [
  {
    id: 'bt-7782', name: '清柠气泡水·瓶身换装', kind: 'object', status: 'done',
    sourceAssetId: 'as-v01', referenceAssetId: 'as-i01',
    prompt: '请将原视频中的玻璃瓶替换为参考图中的铝罐设计，保持冰镇水珠质感。',
    strategy: '保持材质',
    params: { resolution: '1080p', duration: '10 秒', concurrency: 4, retries: 1 },
    outputLocation: 'tos://las-studio/output/obj',
    jobs: { done: 12, running: 0, failed: 0 },
    createdAt: isoDaysAgo(7), updatedAt: isoDaysAgo(6),
    outputUrl: 'https://example.com/output/qning-v1.mp4',
  },
  {
    id: 'bt-7779', name: '雾感唇泥·背景商场', kind: 'scene', status: 'processing',
    sourceAssetId: 'as-v02', referenceAssetId: 'as-i02',
    prompt: '请将原视频背景替换为高级感商场场景，保持人物动作和服装不变。',
    strategy: '保留前景主体',
    params: { resolution: '1080p', duration: '10 秒', concurrency: 4, retries: 1 },
    outputLocation: 'tos://las-studio/output/scene',
    jobs: { done: 5, running: 3, failed: 0 },
    createdAt: isoDaysAgo(0, 8), updatedAt: isoDaysAgo(0, 1),
  },
  {
    id: 'bt-7771', name: '达人 A → 模特 B 替换', kind: 'person', status: 'processing',
    sourceAssetId: 'as-v03', referenceAssetId: 'as-i03',
    prompt: '请将原视频中的达人 A 替换为参考图模特 B 的形象，保留所有动作和表情。',
    strategy: '保留动作姿态',
    params: { resolution: '1080p', duration: '15 秒', concurrency: 8, retries: 2 },
    outputLocation: 'tos://las-studio/output/person',
    jobs: { done: 2, running: 6, failed: 1 },
    createdAt: isoDaysAgo(0, 5), updatedAt: isoDaysAgo(0, 1),
  },
  {
    id: 'bt-7765', name: '冻干猫粮·包装替换', kind: 'object', status: 'failed',
    sourceAssetId: 'as-v04', referenceAssetId: 'as-i04',
    prompt: '将原视频中的旧包装袋替换为新版无纺布手提袋，保留猫咪反应。',
    strategy: '完全替换',
    params: { resolution: '720p', duration: '5 秒', concurrency: 2, retries: 1 },
    outputLocation: 'tos://las-studio/output/obj',
    jobs: { done: 1, running: 0, failed: 4 },
    createdAt: isoDaysAgo(1, 3), updatedAt: isoDaysAgo(1, 2),
    errorMessage: '参考图分辨率过低（< 512px），请重新上传后重试',
  },
  {
    id: 'bt-7760', name: '户外场景批量替换', kind: 'scene', status: 'done',
    sourceAssetId: 'as-v05', referenceAssetId: 'as-i05',
    prompt: '将室内拍摄场景替换为夏日海边/秋日森林/春日公园，输出 3 种风格。',
    strategy: '风格迁移',
    params: { resolution: '2K', duration: '15 秒', concurrency: 4, retries: 1 },
    outputLocation: 'tos://las-studio/output/scene',
    jobs: { done: 8, running: 0, failed: 0 },
    createdAt: isoDaysAgo(2), updatedAt: isoDaysAgo(1, 18),
    outputUrl: 'https://example.com/output/outdoor-set.zip',
  },
  {
    id: 'bt-7754', name: '降噪耳机·主体替换', kind: 'object', status: 'done',
    sourceAssetId: 'as-v06', referenceAssetId: 'as-i06',
    prompt: '将旧款黑色头戴式耳机替换为新款白色入耳式，保持模特姿势。',
    strategy: '完全替换',
    params: { resolution: '1080p', duration: '10 秒', concurrency: 4, retries: 1 },
    outputLocation: 'tos://las-studio/output/obj',
    jobs: { done: 6, running: 0, failed: 0 },
    createdAt: isoDaysAgo(4), updatedAt: isoDaysAgo(3, 22),
    outputUrl: 'https://example.com/output/headphone-v1.mp4',
  },
  {
    id: 'bt-7749', name: '多人物形象替换', kind: 'person', status: 'draft',
    sourceAssetId: 'as-v07', referenceAssetId: 'as-i07',
    prompt: '（草稿）将原视频中的双人达人组合替换为新签约的模特组合。',
    strategy: '完全替换',
    params: { resolution: '1080p', duration: '15 秒', concurrency: 4, retries: 2 },
    outputLocation: '',
    jobs: { done: 0, running: 0, failed: 0 },
    createdAt: isoDaysAgo(5), updatedAt: isoDaysAgo(5),
  },
  {
    id: 'bt-7742', name: '婴儿用品·商品替换', kind: 'object', status: 'processing',
    sourceAssetId: 'as-v08', referenceAssetId: 'as-i08',
    prompt: '将旧款奶瓶替换为新款防胀气奶瓶，保留母婴互动节奏。',
    strategy: '智能融合',
    params: { resolution: '1080p', duration: '10 秒', concurrency: 4, retries: 1 },
    outputLocation: 'tos://las-studio/output/obj',
    jobs: { done: 3, running: 5, failed: 0 },
    createdAt: isoDaysAgo(0, 11), updatedAt: isoDaysAgo(0, 2),
  },
  {
    id: 'bt-7738', name: '美妆达人·面部替换', kind: 'person', status: 'done',
    sourceAssetId: 'as-v09', referenceAssetId: 'as-i09',
    prompt: '将原达人面部替换为签约模特，保留妆容讲解动作。',
    strategy: '仅替换面部',
    params: { resolution: '2K', duration: '15 秒', concurrency: 4, retries: 1 },
    outputLocation: 'tos://las-studio/output/person',
    jobs: { done: 4, running: 0, failed: 0 },
    createdAt: isoDaysAgo(3, 8), updatedAt: isoDaysAgo(3),
    outputUrl: 'https://example.com/output/face-swap-v1.mp4',
  },
  {
    id: 'bt-7731', name: '天空替换·阴转晴', kind: 'scene', status: 'draft',
    sourceAssetId: 'as-v10', referenceAssetId: 'as-i10',
    prompt: '（草稿）将原视频中的阴天天空替换为晴空万里。',
    strategy: '仅替换天空',
    params: { resolution: '1080p', duration: '5 秒', concurrency: 2, retries: 1 },
    outputLocation: '',
    jobs: { done: 0, running: 0, failed: 0 },
    createdAt: isoDaysAgo(6), updatedAt: isoDaysAgo(6),
  },
  {
    id: 'bt-7725', name: '主播·服饰替换', kind: 'person', status: 'failed',
    sourceAssetId: 'as-v11', referenceAssetId: 'as-i11',
    prompt: '将主播身上的卫衣替换为新款连衣裙。',
    strategy: '仅替换服饰',
    params: { resolution: '1080p', duration: '10 秒', concurrency: 4, retries: 2 },
    outputLocation: 'tos://las-studio/output/person',
    jobs: { done: 0, running: 0, failed: 4 },
    createdAt: isoDaysAgo(2, 5), updatedAt: isoDaysAgo(2, 3),
    errorMessage: '原视频遮挡过多，建议降低替换面积或重新拍摄',
  },
  {
    id: 'bt-7718', name: '咖啡店场景·星巴克 → 自营', kind: 'scene', status: 'done',
    sourceAssetId: 'as-v12', referenceAssetId: 'as-i12',
    prompt: '将原视频中的星巴克店面 logo 和元素替换为自营品牌。',
    strategy: '整体替换',
    params: { resolution: '1080p', duration: '10 秒', concurrency: 4, retries: 1 },
    outputLocation: 'tos://las-studio/output/scene',
    jobs: { done: 4, running: 0, failed: 0 },
    createdAt: isoDaysAgo(5, 8), updatedAt: isoDaysAgo(5),
    outputUrl: 'https://example.com/output/cafe-rebrand.mp4',
  },
];

const ASSET_SEED: Asset[] = [
  { id: 'as-v01', kind: 'video', name: 'ref_qning_01.mp4',     objectKey: 'video/ref_qning_01.mp4',     sizeBytes: 19_287_654, sizeText: '18.4 MB', createdAt: isoDaysAgo(7,4) },
  { id: 'as-v02', kind: 'video', name: 'daren_cut_a.mp4',      objectKey: 'video/daren_cut_a.mp4',      sizeBytes: 44_186_112, sizeText: '42.1 MB', createdAt: isoDaysAgo(0,8) },
  { id: 'as-v03', kind: 'video', name: 'opening_hook.mp4',     objectKey: 'video/opening_hook.mp4',     sizeBytes: 10_276_864, sizeText: '9.8 MB',  createdAt: isoDaysAgo(0,5) },
  { id: 'as-v04', kind: 'video', name: 'cat_food_demo.mp4',    objectKey: 'video/cat_food_demo.mp4',    sizeBytes: 35_336_192, sizeText: '33.7 MB', createdAt: isoDaysAgo(1,3) },
  { id: 'as-i01', kind: 'image', name: 'bottle_front.png',     objectKey: 'image/bottle_front.png',     sizeBytes: 1_258_291,  sizeText: '1.2 MB',  createdAt: isoDaysAgo(7,4) },
  { id: 'as-i02', kind: 'image', name: 'model_b_face.jpg',     objectKey: 'image/model_b_face.jpg',     sizeBytes: 901_120,    sizeText: '880 KB',  createdAt: isoDaysAgo(0,5) },
  { id: 'as-i03', kind: 'image', name: 'scene_mall.jpg',       objectKey: 'image/scene_mall.jpg',       sizeBytes: 2_516_582,  sizeText: '2.4 MB',  createdAt: isoDaysAgo(0,8) },
];

const store: BatchTask[] = BATCH_SEED.slice();
const assets: Asset[] = ASSET_SEED.slice();
let counter = 7790;

export const mockClient: ApiClient = {
  async listBatches(req: ListBatchesRequest): Promise<ListBatchesResponse> {
    await delay(120);
    let items = store.slice();
    if (req.kind) items = items.filter((b) => b.kind === req.kind);
    if (req.status) items = items.filter((b) => b.status === req.status);
    if (req.search) {
      const q = req.search.toLowerCase();
      items = items.filter((b) => (b.name + b.id + b.prompt + b.strategy).toLowerCase().includes(q));
    }
    if (req.outputLocation) {
      const q = req.outputLocation.toLowerCase();
      items = items.filter((b) => b.outputLocation.toLowerCase().includes(q));
    }
    return { items: items.slice(0, req.limit ?? 50), nextCursor: null };
  },

  async batchOverview(): Promise<BatchOverviewResponse> {
    await delay(100);
    const running = store.filter((b) => b.status === 'processing');
    const recent = store
      .filter((b) => b.status === 'done' || b.status === 'failed')
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, 4);
    return {
      runningSubJobs: running.reduce((a, b) => a + b.jobs.running, 0),
      recentlyDoneBatches: recent.length,
      running,
      recent,
    };
  },

  async getBatch(req: GetBatchRequest): Promise<GetBatchResponse> {
    await delay(80);
    const found = store.find((b) => b.id === req.id);
    if (!found) throw new ApiError('BATCH_NOT_FOUND', `Batch ${req.id} 不存在`);
    return found;
  },

  async createBatch(req: CreateBatchRequest): Promise<CreateBatchResponse> {
    await delay(150);
    const id = `bt-${++counter}`;
    const now = new Date().toISOString();
    const batch: BatchTask = {
      id, name: req.name, kind: req.kind,
      status: req.intent === 'draft' ? 'draft' : 'processing',
      sourceAssetId: req.sourceAssetId, referenceAssetId: req.referenceAssetId,
      prompt: req.prompt, strategy: req.strategy, params: req.params,
      outputLocation: req.outputLocation,
      jobs: req.intent === 'draft'
        ? { done: 0, running: 0, failed: 0 }
        : { done: 0, running: req.params.concurrency, failed: 0 },
      createdAt: now, updatedAt: now,
    };
    store.unshift(batch);
    return batch;
  },

  async retryBatch(req: RetryBatchRequest): Promise<RetryBatchResponse> {
    await delay(80);
    const b = store.find((x) => x.id === req.id);
    if (!b) throw new ApiError('BATCH_NOT_FOUND', `Batch ${req.id} 不存在`);
    if (b.status !== 'failed' && b.status !== 'done') {
      throw new ApiError('BATCH_INVALID_STATE', '只能重试 failed / done 状态的批次');
    }
    b.status = 'processing';
    b.jobs = { done: 0, running: b.params.concurrency, failed: 0 };
    b.updatedAt = new Date().toISOString();
    delete b.errorMessage;
    return b;
  },

  async cancelBatch(req: CancelBatchRequest): Promise<CancelBatchResponse> {
    await delay(80);
    const b = store.find((x) => x.id === req.id);
    if (!b) throw new ApiError('BATCH_NOT_FOUND', `Batch ${req.id} 不存在`);
    if (b.status !== 'processing') {
      throw new ApiError('BATCH_INVALID_STATE', '只能取消 processing 状态的批次');
    }
    b.status = 'failed';
    b.errorMessage = '用户取消';
    b.jobs = { ...b.jobs, running: 0 };
    b.updatedAt = new Date().toISOString();
    return b;
  },

  async listAssets(req: ListAssetsRequest): Promise<ListAssetsResponse> {
    await delay(100);
    let items = assets.filter((a) => a.kind === req.kind);
    if (req.search) {
      const q = req.search.toLowerCase();
      items = items.filter((a) => a.name.toLowerCase().includes(q));
    }
    return { items: items.slice(0, req.limit ?? 50), nextCursor: null };
  },

  async requestUpload(req: RequestUploadUrlRequest): Promise<RequestUploadUrlResponse> {
    await delay(60);
    const id = `as-up${++assetCounter}`;
    const objectKey = `${req.kind}/${Date.now()}-${req.fileName}`;
    pendingUploads.set(id, { kind: req.kind, fileName: req.fileName, objectKey, contentType: req.contentType });
    return {
      uploadUrl: `mock://upload/${id}`,
      objectKey,
      assetId: id,
      expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
    };
  },

  async confirmUpload(req: ConfirmUploadRequest): Promise<ConfirmUploadResponse> {
    await delay(80);
    const pending = pendingUploads.get(req.assetId);
    if (!pending) throw new ApiError('ASSET_NOT_FOUND', `pending upload ${req.assetId} 不存在或已过期`);
    pendingUploads.delete(req.assetId);
    const sizeBytes = req.sizeBytes ?? 0;
    const asset: Asset = {
      id: req.assetId,
      kind: pending.kind,
      name: pending.fileName,
      objectKey: pending.objectKey,
      sizeBytes,
      sizeText: formatBytes(sizeBytes),
      createdAt: new Date().toISOString(),
    };
    assets.unshift(asset);
    return asset;
  },

  async getConfig(): Promise<AppConfig> {
    await delay(60);
    return AppConfigSchema.parse(serverConfig);
  },

  async putConfig(cfg: AppConfig): Promise<Ok> {
    await delay(80);
    serverConfig = cfg;
    return { ok: true };
  },

  // mock 模式：演示用，不做邀请码门禁，永远已激活 + 充足额度
  async getMe(): Promise<GetMeResponse> {
    await delay(40);
    return { userId: 'u-mock', activated: true, credits: mockCredits, inviteCode: 'DEMO2026' };
  },

  async redeemInvite(_req: RedeemInviteRequest): Promise<RedeemInviteResponse> {
    await delay(60);
    mockCredits += 10;
    return { userId: 'u-mock', activated: true, credits: mockCredits, inviteCode: 'DEMO2026' };
  },
};

let mockCredits = 999;

let assetCounter = 100;
type PendingUpload = { kind: Asset['kind']; fileName: string; objectKey: string; contentType: string };
const pendingUploads = new Map<string, PendingUpload>();
let serverConfig: AppConfig = AppConfigSchema.parse({});

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
